import localforage from 'localforage';

const STORAGE_KEY = 'users_v1';

// The user data is held as a static JSON file. If the data is not present in the local store (on the 
// first run, or if the data has been reset), it is reloaded from the file. This means that to change
// the data, you may need to both change the static data file AND run the "Clear Local Data" page 
// under Admin => Advanced settings.
// There is one exception to this: when a user logs in for the first time, if their user record is not
// present in the local store, it is added at that time and not cleared by the reset function, because 
// we don't want to erase the simulated user record for the presently logged-in user.
const loadDefaults = async () => {
  try {
    const defaults = require('./_local/_usersData.json');
    return defaults || [];
  } catch (e) {
    return [];
  }
};

const read = async () => {
  try {
    const raw = await localforage.getItem(STORAGE_KEY);
    if (!raw) {
      const defaults = await loadDefaults();
      await localforage.setItem(STORAGE_KEY, defaults);
      return defaults;
    }
    return raw;
  } catch (e) {
    return [];
  }
};

const write = async (data) => {
  await localforage.setItem(STORAGE_KEY, data);
};

// This is called by the "Clear Local Data" page under Admin => Advanced settings
export const resetToDefaults = async () => {
  const defaults = await loadDefaults();
  await write(defaults);
  return defaults;
};

// We use this to display all current users in the user admin pages. This allows us 
// to simulate user maintenance in the demo, using a dynamic user data store
export const getAllUsers = async () => await read();

// We use this to check for a user record when a user logs in. If the record is not found,
// we create it at that time.
export const getUserByEmail = async (email) => {
  if (!email) return null;
  const data = await read();
  const lower = String(email).toLowerCase();
  return data.find(u => u && (String(u.emailAddress || u.userName || '').toLowerCase() === lower));
};

// This is used to update a user's details (first name, last name, disabled status) in the user store.
// It is called from the user maintenance page
export const patchUser = async (userPatch) => {
  if (!userPatch || !userPatch.emailAddress) return false;
  const data = await read();
  const lower = String(userPatch.emailAddress).toLowerCase();
  const idx = data.findIndex(u => u && (String(u.emailAddress || '').toLowerCase() === lower));
  if (idx === -1) return false;
  const updated = {
    ...data[idx],
    firstName: userPatch.firstName,
    lastName: userPatch.lastName,
    disabled: userPatch.disabled,
  };
  data[idx] = updated;
  await write(data);
  return updated;
};

// This is used to add a new user to the store. It is called when a user logs in for the first
// time if they do not already have a user record, and in the user maintenance page
export const addUser = async (user) => {
  const data = await read();
  data.push(user);
  await write(data);
  return user;
};

// This is used to delete a user from the store. It is called from the user maintenance page.
export const deleteUser = async (id) => {
  const data = await read();
  const filtered = data.filter(u => !(u && (u.id === id || String(u.id) === String(id))));
  await write(filtered);
  return filtered.length !== data.length;
};

// If we're not running locally, we get the new user ID from an identity field on the database, but in local mode,
// we need to simulate it by getting the highest user ID presently in the store, and incrementing it by 1.
export const getNextUserId = async () => {
  try {
    const items = await read();
    if (!items || items.length === 0) return 1;
    let max = -Infinity;
    for (const u of items) {
      if (!u) continue;
      const rawId = u.id ?? u.userId ?? u.ID;
      const num = Number(rawId);
      if (!Number.isNaN(num) && isFinite(num)) {
        if (num > max) max = num;
      }
    }
    if (max === -Infinity) return 1;
    return max + 1;
  } catch (e) {
    return 1;
  }
};

export default {
  getAllUsers,
  getUserByEmail,
  patchUser,
  addUser,
  deleteUser,
  getNextUserId,
  resetToDefaults,
};
